export const REFINE_BOQ_STATE_CODE = {
  PENDING: 'REFINE_BOQ_PENDING',
  STARTED: 'REFINE_BOQ_STARTED',
  REVIEW_READY: 'REFINE_BOQ_REVIEW_READY',
  REVIEW_COMPLETE: 'REFINE_BOQ_REVIEW_COMPLETE',
  NO_CHANGES: 'REFINE_BOQ_NO_CHANGES',
  FAILURE: 'REFINE_BOQ_FAILURE',
  NOT_FOUND: 'REFINE_BOQ_NOT_FOUND',
  REQUEST_FAILED: 'REFINE_BOQ_REQUEST_FAILED',
  POLL_FAILED: 'REFINE_BOQ_POLL_FAILED',
  INVALID_RESPONSE: 'REFINE_BOQ_INVALID_RESPONSE',
  UNSUPPORTED_STRUCTURAL_CHANGES:
    'REFINE_BOQ_UNSUPPORTED_STRUCTURAL_CHANGES',
};

/** Runs QSAI analysed end to end — the instruction is recorded, so the input can reset. */
export const REFINE_ANALYSED_STATE_CODES = [
  REFINE_BOQ_STATE_CODE.REVIEW_READY,
  REFINE_BOQ_STATE_CODE.NO_CHANGES,
  REFINE_BOQ_STATE_CODE.UNSUPPORTED_STRUCTURAL_CHANGES,
];

/** Later polls must not rebuild a review the user is already resolving. */
export const REFINE_SETTLED_STATE_CODES = [
  ...REFINE_ANALYSED_STATE_CODES,
  REFINE_BOQ_STATE_CODE.REVIEW_COMPLETE,
];

/** Nothing was analysed, so the same instruction can be submitted again. */
export const REFINE_RETRYABLE_STATE_CODES = [
  REFINE_BOQ_STATE_CODE.FAILURE,
  REFINE_BOQ_STATE_CODE.NOT_FOUND,
  REFINE_BOQ_STATE_CODE.REQUEST_FAILED,
  REFINE_BOQ_STATE_CODE.POLL_FAILED,
  REFINE_BOQ_STATE_CODE.INVALID_RESPONSE,
];

export const REFINE_BOQ_EDITABLE_FIELDS = [
  'item_no',
  'description',
  'quantity',
  'unit_id',
  'budget_rate',
  'budget_total',
  'tenderee_note',
];

export const REFINE_BOQ_STRUCTURAL_DIFF_FIELDS = [
  'deleted',
  'swapped',
  'added',
  'moved',
];

/** QSAI rejects shorter instructions (`prompt must be at least 20 character(s)`). */
export const REFINE_PROMPT_MIN_LENGTH = 20;

const GENERIC_HTTP_STATUS_TEXTS = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'internal server error',
  'service unavailable',
  'conflict',
  'unprocessable entity',
  'too many requests',
]);

export const isRefinePromptReady = (prompt) =>
  String(prompt ?? '').trim().length >= REFINE_PROMPT_MIN_LENGTH;

const tryParseJson = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const asTrimmedString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const isUserFacingRefineErrorMessage = (message) => {
  const text = asTrimmedString(message);
  if (!text) return false;
  if (text.startsWith('{') || text.startsWith('[')) return false;
  if (GENERIC_HTTP_STATUS_TEXTS.has(text.toLowerCase())) return false;
  return true;
};

export const isRefineValidationError = ({
  apiErrorCode,
  apiErrorType,
} = {}) =>
  apiErrorType === 'validation_error' ||
  apiErrorCode === 'INVALID_JSON' ||
  apiErrorCode === 'VALIDATION_ERROR';

/**
 * Reads a user-facing message from the gateway body, including the nested JSON
 * string the generic `"error"` handler used to emit for refine-boq 400s.
 */
export const parseRefineApiError = (error) => {
  const response = error?.response ?? error ?? {};
  let payload = tryParseJson(response.error ?? response);
  payload = tryParseJson(payload);

  if (payload && typeof payload === 'object' && payload.error) {
    const inner = tryParseJson(payload.error);
    if (inner && typeof inner === 'object') {
      payload = inner;
    }
  }

  const system =
    payload && typeof payload === 'object' ? payload.errors?.system : null;
  const hasOwnMessage = Boolean(asTrimmedString(payload?.message));
  const nestedError =
    payload && typeof payload === 'object' && hasOwnMessage ? payload : null;

  const message =
    asTrimmedString(nestedError?.message) ||
    asTrimmedString(payload?.message) ||
    asTrimmedString(system?.user_message) ||
    asTrimmedString(system?.details?.validation_message) ||
    asTrimmedString(error?.message);

  const code =
    (typeof nestedError?.code === 'string' && nestedError.code) ||
    (typeof payload?.code === 'string' && payload.code) ||
    (typeof system?.code === 'string' && system.code) ||
    (typeof error?.response?.error?.code === 'string' &&
      error.response.error.code) ||
    null;

  const type =
    asTrimmedString(nestedError?.type) ||
    asTrimmedString(payload?.type) ||
    asTrimmedString(system?.type) ||
    null;

  return {
    code,
    type,
    message: isUserFacingRefineErrorMessage(message) ? message : null,
  };
};

const NUMERIC_FIELDS = new Set([
  'quantity',
  'unit_id',
  'budget_rate',
  'budget_total',
]);

const normalizeComparableValue = (field, value) => {
  if (NUMERIC_FIELDS.has(field)) {
    if (value == null || value === '') return 0;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : value;
  }
  return value == null ? '' : String(value);
};

const resolveUnitSymbol = (row, units) => {
  if (row?.unit != null && row.unit !== '') return String(row.unit);
  const match = (units || []).find(
    (option) =>
      row?.unit_id != null &&
      Number(option?.value ?? option?.id) === Number(row.unit_id)
  );
  return String(match?.symbol ?? match?.label ?? '');
};

/** BoQ rows in the field set QSAI returns in `result.entries`, matched by `position`. */
export const serializeBoqEntriesForRefinement = (rows, units = []) =>
  (rows || []).map((row) => ({
    position: row?.position != null ? Number(row.position) : null,
    item_no: row?.item_no ?? '',
    description: row?.description ?? '',
    type: row?.type ?? '',
    quantity: Number(row?.quantity ?? 0),
    unit: resolveUnitSymbol(row, units),
    unit_id: row?.unit_id != null ? Number(row.unit_id) : null,
    budget_rate: Number(row?.budget_rate ?? 0),
    budget_total: Number(row?.budget_total ?? 0),
    tenderee_note: row?.tenderee_note ?? '',
  }));

/**
 * `boq_entity` is a JSON string BoqMiddleware::relayRefineToQsai() writes verbatim to the
 * boq.json file it forwards to QSAI — the request is rejected when it is missing.
 */
export const buildRefineBoqFormData = ({
  prompt,
  packageName,
  entries = [],
}) => {
  const body = new FormData();
  body.append('prompt', prompt ?? '');
  body.append('package_name', packageName ?? '');
  body.append('boq_entity', JSON.stringify({ entries }));
  return body;
};

export const bumpRefinePollGeneration = (state, packageId) => {
  if (packageId == null) return;
  state.refinePollGenerationById = state.refinePollGenerationById || {};
  const current = state.refinePollGenerationById[packageId] ?? 0;
  state.refinePollGenerationById[packageId] = current + 1;
};

export const isStaleRefinePollResponse = (
  state,
  packageId,
  pollGeneration
) => {
  if (packageId == null) return true;
  if (pollGeneration == null) return false;
  return pollGeneration !== state.refinePollGenerationById?.[packageId];
};

export const isNonEmptyDiffMap = (value) => {
  if (value == null) return false;
  if (Array.isArray(value) || typeof value === 'string') {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  return Boolean(value);
};

export const normalizeRefineBoqResult = (rawResult) => {
  if (
    !rawResult ||
    !Array.isArray(rawResult.entries) ||
    !rawResult.diff ||
    typeof rawResult.diff !== 'object'
  ) {
    return null;
  }

  const requiredDiffFields = [
    'updated',
    ...REFINE_BOQ_STRUCTURAL_DIFF_FIELDS,
  ];
  if (!requiredDiffFields.every((field) => field in rawResult.diff)) {
    return null;
  }

  return {
    entries: rawResult.entries,
    diff: requiredDiffFields.reduce(
      (diff, field) => ({ ...diff, [field]: rawResult.diff[field] }),
      {}
    ),
  };
};

export const hasStructuralRefineChanges = (result) =>
  REFINE_BOQ_STRUCTURAL_DIFF_FIELDS.some((field) =>
    isNonEmptyDiffMap(result?.diff?.[field])
  );

const getRowPosition = (row) => {
  if (row?.position == null || row.position === '') return null;
  const numeric = Number(row?.position);
  return Number.isFinite(numeric) ? numeric : null;
};

const resolveProposedFieldValue = (field, apiRow, currentRow, units) => {
  if (field !== 'unit_id') return apiRow[field];
  if (apiRow.unit_id != null) return apiRow.unit_id;
  if (apiRow.unit == null || apiRow.unit === '') return currentRow.unit_id;
  const unit = (units || []).find(
    (option) =>
      String(option?.symbol || option?.label || '')
        .trim()
        .toLowerCase() === String(apiRow.unit).trim().toLowerCase()
  );
  return unit?.value ?? unit?.id ?? currentRow.unit_id;
};

/**
 * Builds a review without applying it. API rows are matched to current draft
 * rows by position, and only editable cells are copied into proposed rows.
 */
export const buildRefineBoqReview = ({
  instruction,
  currentRows,
  result,
  units = [],
}) => {
  if (!Array.isArray(currentRows) || !result?.entries) return null;

  const currentByPosition = new Map();
  for (const row of currentRows) {
    const position = getRowPosition(row);
    if (position == null || currentByPosition.has(position)) return null;
    currentByPosition.set(position, row);
  }

  const resultPositions = new Set();
  const updatedPositions = new Set(
    Object.keys(result?.diff?.updated || {}).map(Number)
  );
  const rows = [];

  for (const apiRow of result.entries) {
    const position = getRowPosition(apiRow);
    if (position == null || resultPositions.has(position)) return null;
    resultPositions.add(position);

    const currentRow = currentByPosition.get(position);
    if (!currentRow) return null;

    if (updatedPositions.has(position)) {
      const changedCells = REFINE_BOQ_EDITABLE_FIELDS.reduce(
        (cells, field) => {
          const before = currentRow[field];
          const after = resolveProposedFieldValue(
            field,
            apiRow,
            currentRow,
            units
          );
          if (
            normalizeComparableValue(field, before) !==
            normalizeComparableValue(field, after)
          ) {
            cells.push({ field, before, after });
          }
          return cells;
        },
        []
      );

      if (changedCells.length) {
        const proposedRow = changedCells.reduce(
          (row, cell) => ({ ...row, [cell.field]: cell.after }),
          { ...currentRow }
        );
        rows.push({
          rowId: currentRow.id,
          position,
          changedCells,
          proposedRow,
        });
      }
    }
  }

  if (resultPositions.size !== currentByPosition.size) return null;

  return {
    instruction,
    rows,
  };
};

export const getBoqRowsForRefinement = (state, packageId) => {
  const entity =
    state.entities?.find(
      (candidate) => Number(candidate.id) === Number(packageId)
    ) ||
    (Number(state.entity?.id) === Number(packageId) ? state.entity : null);

  if (!entity) return null;
  return Array.isArray(entity.nextEntries) ? entity.nextEntries : entity.entries;
};
